import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Nome da coleção no Firestore onde serão salvos os dados
 */
const COLLECTION_NAME = 'avaliacoes';

/**
 * Salva uma nova avaliação no Firestore
 *
 * @param {Object} evaluationData - Dados da avaliação
 * @param {number} evaluationData.rating - Nota de 1 a 5
 * @param {Array<string>} evaluationData.selectedOptions - Opções selecionadas
 * @param {string} evaluationData.comment - Comentário opcional
 * @param {string} [evaluationData.userId] - ID do usuário (opcional)
 * @param {string} [evaluationData.courseName] - Nome do projeto (opcional)
 * @param {string} [evaluationData.configId] - ID da configuração (opcional)
 * @param {boolean} [evaluationData.isScorm] - Se é um pacote SCORM (opcional)
 * @returns {Promise<Object>} Objeto com sucesso e ID do documento
 */
export const saveEvaluation = async (evaluationData) => {
  try {
    // Prepara os dados para salvar
    const dataToSave = {
      rating: evaluationData.rating,
      selectedOptions: evaluationData.selectedOptions || [],
      comment: evaluationData.comment || '',
      userId: evaluationData.userId || 'anonimo',
      courseName: evaluationData.courseName || null,
      configId: evaluationData.configId || null, // Adiciona o ID da configuração
      isScorm: evaluationData.isScorm || false, // Indica se é SCORM
      createdAt: serverTimestamp(), // Timestamp do servidor
      userAgent: navigator.userAgent, // Informações do navegador
      timestamp: new Date().toISOString(), // Timestamp local
    };

    // Salva no Firestore
    const docRef = await addDoc(collection(db, COLLECTION_NAME), dataToSave);

    // console.log('✅ Avaliação salva com sucesso! ID:', docRef.id);

    return {
      success: true,
      id: docRef.id,
      message: 'Avaliação salva com sucesso!',
    };
  } catch (error) {
    console.error('❌ Erro ao salvar avaliação:', error);

    return {
      success: false,
      error: error.message,
      message: 'Erro ao salvar avaliação. Tente novamente.',
    };
  }
};

/**
 * Busca avaliações por ID do projeto
 *
 * @param {string} courseName - ID do projeto
 * @param {number} maxResults - Número máximo de resultados (padrão: 100)
 * @returns {Promise<Array>} Lista de avaliações
 */
export const getEvaluationsByCourse = async (courseName, maxResults = 100) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('courseName', '==', courseName),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );

    const querySnapshot = await getDocs(q);
    const evaluations = [];

    querySnapshot.forEach((doc) => {
      evaluations.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return evaluations;
  } catch (error) {
    console.error('Erro ao buscar avaliações:', error);
    return [];
  }
};

/**
 * Busca avaliações por ID do usuário
 *
 * @param {string} userId - ID do usuário
 * @returns {Promise<Array>} Lista de avaliações do usuário
 */
export const getEvaluationsByUser = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const evaluations = [];

    querySnapshot.forEach((doc) => {
      evaluations.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return evaluations;
  } catch (error) {
    console.error('Erro ao buscar avaliações do usuário:', error);
    return [];
  }
};

/**
 * Busca avaliações por ID da configuração
 *
 * @param {string} configId - ID da configuração
 * @param {number} maxResults - Número máximo de resultados (padrão: 100)
 * @returns {Promise<Array>} Lista de avaliações
 */
export const getEvaluationsByConfig = async (configId, maxResults = 100) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('configId', '==', configId),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );

    const querySnapshot = await getDocs(q);
    const evaluations = [];

    querySnapshot.forEach((doc) => {
      evaluations.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return evaluations;
  } catch (error) {
    console.error('Erro ao buscar avaliações da configuração:', error);
    return [];
  }
};

/**
 * Calcula a média de avaliações de um projeto
 *
 * @param {string} courseName - ID do projeto
 * @returns {Promise<Object>} Objeto com estatísticas
 */
export const getCourseStats = async (courseName) => {
  try {
    const evaluations = await getEvaluationsByCourse(courseName, 1000);

    if (evaluations.length === 0) {
      return {
        totalEvaluations: 0,
        averageRating: 0,
        ratings: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      };
    }

    const ratings = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let totalRating = 0;

    evaluations.forEach((evaluation) => {
      const rating = evaluation.rating;
      ratings[rating]++;
      totalRating += rating;
    });

    return {
      totalEvaluations: evaluations.length,
      averageRating: (totalRating / evaluations.length).toFixed(2),
      ratings,
      latestEvaluations: evaluations.slice(0, 5),
    };
  } catch (error) {
    console.error('Erro ao calcular estatísticas:', error);
    return null;
  }
};

export default {
  saveEvaluation,
  getEvaluationsByCourse,
  getEvaluationsByUser,
  getEvaluationsByConfig,
  getCourseStats,
};
